/**
 * /hotels/mine — User's Hotelbeds bookings list.
 *
 * Standalone list for hotel bookings so users can find, revisit,
 * and (Phase 3) manage their reservations independently of the
 * broader "booked trips" feature which spans multiple providers.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BedDouble, Calendar, Download, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";
import HotelCancelModal from "@/components/hotels/HotelCancelModal";

export default function HotelBookingsMinePage() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/hotels/bookings/mine")
      .then(({ data }) => setBookings(data.bookings || []))
      .catch(() => setBookings([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="hotels-mine-page" className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-6 pb-16">
        <button
          onClick={() => nav("/hotels")}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border-2 border-tb-navy/20 font-hand text-tb-navy/70 tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> back
        </button>

        <div className="mt-6 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-tb-navy text-3xl md:text-4xl leading-tight font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
              My hotel bookings
            </h1>
            <p className="mt-1 font-hand text-tb-navy/60 text-sm italic">
              Every hotel you&apos;ve confirmed on TravelBio.
            </p>
          </div>
          <PoweredByHotelbeds />
        </div>

        {loading ? (
          <p className="mt-16 text-center font-hand text-tb-navy/60">~ loading ~</p>
        ) : bookings.length === 0 ? (
          <div data-testid="hotels-mine-empty" className="mt-14 text-center py-14 rounded-2xl border-2 border-tb-cream-3">
            <BedDouble className="w-14 h-14 text-tb-navy/25 mx-auto" strokeWidth={1.4} />
            <p className="mt-3 font-serif-it text-tb-navy text-xl font-semibold">No hotel bookings yet</p>
            <p className="mt-1 font-hand text-tb-navy/60 text-sm">Search for a stay and book directly with Hotelbeds.</p>
            <button
              type="button"
              onClick={() => nav("/hotels")}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap"
            >
              <BedDouble className="w-4 h-4" /> Book a hotel
            </button>
          </div>
        ) : (
          <ul className="mt-6 space-y-3">
            {bookings.map((b) => {
              const cancelled = (b.status || "").toUpperCase() === "CANCELLED";
              const voucherUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/hotels/bookings/${encodeURIComponent(b.hotelbeds_reference)}/voucher.pdf`;
              return (
                <li key={b.hotelbeds_reference}>
                  <div
                    data-testid={`hotels-mine-card-${b.hotelbeds_reference}`}
                    className={"rounded-2xl border-2 shadow-sm overflow-hidden p-4 flex items-center gap-3 transition " +
                      (cancelled ? "border-red-200 bg-red-50/40" : "border-tb-cream-3 bg-white hover:border-tb-navy")
                    }
                  >
                    <button
                      type="button"
                      onClick={() => nav(`/hotels/booking/${encodeURIComponent(b.hotelbeds_reference)}`)}
                      className="flex-1 min-w-0 text-left flex items-center gap-3"
                    >
                      <div className={"w-12 h-12 rounded-full flex items-center justify-center shrink-0 " + (cancelled ? "bg-red-100" : "bg-tb-navy-deep")}>
                        <BedDouble className={"w-6 h-6 " + (cancelled ? "text-red-700" : "text-tb-gold-bright")} strokeWidth={1.6} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-serif text-tb-navy text-lg truncate" style={{ fontFamily: '"Playfair Display", serif' }}>
                          {b.hotel_name}
                        </div>
                        <div className="font-hand text-tb-navy/60 text-xs flex items-center gap-1.5">
                          <Calendar className="w-3 h-3" /> {b.check_in} → {b.check_out} · {b.total_price} {b.currency}
                        </div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          {cancelled ? (
                            <span data-testid={`mine-status-cancelled-${b.hotelbeds_reference}`} className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-hand bg-red-100 border border-red-300 text-red-800">
                              Cancelled
                            </span>
                          ) : (
                            <PoweredByHotelbeds size="xs" />
                          )}
                          <span className="font-hand text-tb-navy/45 text-[10px] uppercase tracking-widest">
                            Ref {b.hotelbeds_reference}
                          </span>
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      <a
                        href={voucherUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`hotels-mine-voucher-${b.hotelbeds_reference}`}
                        title="Download voucher"
                        className="inline-flex items-center gap-1 rounded-full border-2 border-tb-cream-3 hover:border-tb-navy text-tb-navy/70 hover:text-tb-navy px-3 py-1.5 tb-tap"
                      >
                        <Download className="w-4 h-4" />
                        <span className="font-hand text-xs hidden sm:inline">Voucher</span>
                      </a>
                      {!cancelled && (
                        <button
                          type="button"
                          data-testid={`hotels-mine-cancel-${b.hotelbeds_reference}`}
                          onClick={(e) => { e.stopPropagation(); setCancelTarget(b); }}
                          className="inline-flex items-center gap-1 rounded-full border-2 border-red-200 hover:border-red-400 text-red-700 px-3 py-1.5 tb-tap"
                        >
                          <XCircle className="w-4 h-4" />
                          <span className="font-hand text-xs hidden sm:inline">Cancel</span>
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {cancelTarget && (
          <HotelCancelModal
            reference={cancelTarget.hotelbeds_reference}
            hotelName={cancelTarget.hotel_name}
            onClose={() => setCancelTarget(null)}
            onCancelled={() => { setCancelTarget(null); load(); }}
          />
        )}
      </div>
    </div>
  );
}
